import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { Pagination } from '../types/common/Pagination';
import type {
  SupportTicketCategoryViewModel,
  SupportTicketStatusViewModel,
  SupportTicketViewModel,
  SupportTicketCreateModel,
} from '../types/support-ticket';
import { buildPaginationParams } from '../client/queryParams';
import { apiClient } from '../client/client';

const USER_BASE = '/api/support-tickets';
const ADMIN_BASE = '/api/admin/support-tickets';

export interface SupportTicketListParams {
  pagination?: Pagination | null;
  supportStatusId?: number | string | null;
  supportCategoryId?: number | string | null;
}

export class SupportTicketService {
  constructor(private readonly client: AxiosInstance = apiClient) {}

  async getCategories(): Promise<ServiceResultT<SupportTicketCategoryViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<SupportTicketCategoryViewModel[]>>(
      `${USER_BASE}/categories`
    );
    return data;
  }

  async getStatuses(): Promise<ServiceResultT<SupportTicketStatusViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<SupportTicketStatusViewModel[]>>(
      `${USER_BASE}/statuses`
    );
    return data;
  }

  async create(payload: SupportTicketCreateModel): Promise<ServiceResultT<SupportTicketViewModel>> {
    const { data } = await this.client.post<ServiceResultT<SupportTicketViewModel>>(
      USER_BASE,
      payload
    );
    return data;
  }

  async getMyTickets(params?: { pagination?: Pagination | null }): Promise<
    ServiceResultT<SupportTicketViewModel[]>
  > {
    const query = params?.pagination ? buildPaginationParams(params.pagination) : {};
    const { data } = await this.client.get<ServiceResultT<SupportTicketViewModel[]>>(
      `${USER_BASE}/my`,
      { params: query }
    );
    return data;
  }

  async getMyTicketById(id: string): Promise<ServiceResultT<SupportTicketViewModel>> {
    const { data } = await this.client.get<ServiceResultT<SupportTicketViewModel>>(
      `${USER_BASE}/my/${id}`
    );
    return data;
  }

  /** Admin: get single ticket by id (GET /api/admin/support-tickets/:id) */
  async getAdminTicketById(id: string): Promise<ServiceResultT<SupportTicketViewModel>> {
    const { data } = await this.client.get<ServiceResultT<SupportTicketViewModel>>(
      `${ADMIN_BASE}/${id}`
    );
    return data;
  }

  /** Admin: paginated list with optional filters */
  async getAdminTickets(
    params?: SupportTicketListParams
  ): Promise<ServiceResultT<SupportTicketViewModel[]>> {
    const query: Record<string, unknown> = {};
    if (params?.pagination) Object.assign(query, buildPaginationParams(params.pagination));
    if (params?.supportStatusId != null) query['SupportStatusId'] = params.supportStatusId;
    if (params?.supportCategoryId != null) query['SupportCategoryId'] = params.supportCategoryId;
    const { data } = await this.client.get<ServiceResultT<SupportTicketViewModel[]>>(
      ADMIN_BASE,
      { params: query }
    );
    return data;
  }

  /** Admin: start support – creates/assigns conversation, returns updated ticket or conversationId */
  async startSupport(ticketId: string): Promise<ServiceResultT<SupportTicketViewModel>> {
    const { data } = await this.client.post<ServiceResultT<SupportTicketViewModel>>(
      `${ADMIN_BASE}/${ticketId}/start`
    );
    return data;
  }

  /** Admin: resolve and close ticket */
  async resolve(ticketId: string): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${ADMIN_BASE}/${ticketId}/resolve`
    );
    return data;
  }
}

export const supportTicketService = new SupportTicketService();
